import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  listAll,
  UploadMetadata,
} from 'firebase/storage';
import { storage } from '../config/firebase';

export interface UploadProgressCallback {
  (progress: number, bytesTransferred: number, totalBytes: number): void;
}

export interface UploadResult {
  url: string;
  fullPath: string;
  name: string;
  size: number;
  contentType: string;
}

class StorageService {
  /**
   * Checks if Firebase Storage is initialized and available
   */
  public isStorageAvailable(): boolean {
    return Boolean(storage);
  }

  /**
   * Uploads a file to Firebase Cloud Storage under structured paths:
   * - /sites/{siteId}/branding/{fileName}
   * - /sites/{siteId}/media/{fileName}
   * - /sites/{siteId}/yachts/{yachtId}/{fileName}
   * - /sites/{siteId}/blog/{postId}/{fileName}
   * - /users/{userId}/avatar/{fileName}
   */
  public async uploadFile(
    path: string,
    file: File | Blob,
    metadata?: UploadMetadata,
    onProgress?: UploadProgressCallback
  ): Promise<UploadResult> {
    if (!this.isStorageAvailable() || !storage) {
      // Offline / Local Blob URL fallback
      const objectUrl = URL.createObjectURL(file);
      const fileName = file instanceof File ? file.name : 'upload.bin';
      return {
        url: objectUrl,
        fullPath: path,
        name: fileName,
        size: file.size,
        contentType: file.type || 'application/octet-stream',
      };
    }

    const fileRef = storageRef(storage, path);
    const uploadTask = uploadBytesResumable(fileRef, file, metadata);

    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          if (onProgress) {
            onProgress(progress, snapshot.bytesTransferred, snapshot.totalBytes);
          }
        },
        (error) => {
          console.error('[Firebase Storage] Upload error:', error);
          reject(error);
        },
        async () => {
          try {
            const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
            resolve({
              url: downloadUrl,
              fullPath: uploadTask.snapshot.ref.fullPath,
              name: uploadTask.snapshot.ref.name,
              size: uploadTask.snapshot.totalBytes,
              contentType: metadata?.contentType || file.type || '',
            });
          } catch (urlError) {
            reject(urlError);
          }
        }
      );
    });
  }

  /**
   * Uploads site logo or favicon to /sites/{siteId}/branding/{fileName}
   */
  public async uploadSiteBranding(
    siteId: string,
    file: File,
    type: 'logo' | 'favicon' | 'ogImage' = 'logo'
  ): Promise<UploadResult> {
    const ext = file.name.split('.').pop() || 'png';
    const path = `sites/${siteId}/branding/${type}_${Date.now()}.${ext}`;
    return this.uploadFile(path, file, { contentType: file.type });
  }

  /**
   * Uploads general media to /sites/{siteId}/media/{fileName}
   */
  public async uploadSiteMedia(
    siteId: string,
    file: File,
    onProgress?: UploadProgressCallback
  ): Promise<UploadResult> {
    const cleanFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `sites/${siteId}/media/${Date.now()}_${cleanFileName}`;
    return this.uploadFile(path, file, { contentType: file.type }, onProgress);
  }

  /**
   * Uploads yacht fleet images to /sites/{siteId}/yachts/{yachtId}/{fileName}
   */
  public async uploadYachtImage(
    siteId: string,
    yachtId: string,
    file: File
  ): Promise<UploadResult> {
    const cleanFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `sites/${siteId}/yachts/${yachtId}/${Date.now()}_${cleanFileName}`;
    return this.uploadFile(path, file, { contentType: file.type });
  }

  /**
   * Uploads blog cover / inline images to /sites/{siteId}/blog/{postId}/{fileName}
   */
  public async uploadBlogImage(
    siteId: string,
    postId: string,
    file: File
  ): Promise<UploadResult> {
    const cleanFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `sites/${siteId}/blog/${postId}/${Date.now()}_${cleanFileName}`;
    return this.uploadFile(path, file, { contentType: file.type });
  }

  /**
   * Uploads user profile avatar to /users/{userId}/avatar/{fileName}
   */
  public async uploadUserAvatar(
    userId: string,
    file: File
  ): Promise<UploadResult> {
    const ext = file.name.split('.').pop() || 'jpg';
    const path = `users/${userId}/avatar/avatar_${Date.now()}.${ext}`;
    return this.uploadFile(path, file, { contentType: file.type });
  }

  /**
   * Deletes a file from Firebase Storage
   */
  public async deleteFile(fullPath: string): Promise<boolean> {
    if (!this.isStorageAvailable() || !storage) return true;
    try {
      const fileRef = storageRef(storage, fullPath);
      await deleteObject(fileRef);
      return true;
    } catch (err) {
      console.warn('[Firebase Storage] Delete file error:', err);
      return false;
    }
  }

  /**
   * Recursively deletes all files within a site's storage directory (/sites/{siteId}/)
   * Ensures website-specific media, brand assets, and yacht images are physically cleaned.
   */
  public async deleteSiteFolder(siteId: string): Promise<boolean> {
    if (!this.isStorageAvailable() || !storage || !siteId) return true;
    try {
      const folderRef = storageRef(storage, `sites/${siteId}`);
      const deleteRecursively = async (ref: any): Promise<void> => {
        try {
          const res = await listAll(ref);
          await Promise.all(
            res.items.map(async (itemRef) => {
              try {
                await deleteObject(itemRef);
              } catch (delErr) {
                console.warn('[Firebase Storage] Item delete notice:', delErr);
              }
            })
          );
          await Promise.all(res.prefixes.map((prefixRef) => deleteRecursively(prefixRef)));
        } catch (listErr) {
          // Folder may not exist if no assets were uploaded yet
          console.warn('[Firebase Storage] List folder notice:', listErr);
        }
      };
      await deleteRecursively(folderRef);
      return true;
    } catch (err) {
      console.warn('[Firebase Storage] Delete site folder error:', err);
      return false;
    }
  }
}

export const storageService = new StorageService();
export default storageService;
